import {
  BuilderView,
  FormattedResponseItem,
} from 'src/pages/workflow-builder-edit/types';
import {
  GenericToolPayload,
  VoiceTranscriptPayload,
} from 'src/pages/workflow-builder-preview/voice-preview/type';
import { ContextVariableValue } from 'src/types/workflowBuilderAPITypes';

export enum PreviewLogType {
  AI_INSTRUCTION = 'ai_instruction',
  API_STEP = 'api_step',
  ARTICLE_SUGGESTION_STEP = 'article_suggestion_step',
  AUTOFLOW = 'autoflow',
  AUTOFLOW_TOOL = 'autoflow_tool',
  CODE_EXECUTION_STEP = 'code_execution_step',
  CONDITION = 'condition',
  CONTENT_SNIPPET = 'content_snippet',
  CUSTOM_EVENT = 'custom_event',
  ERROR = 'error',
  GOTO_STEP = 'goto_step',
  IMAGE_ATTACHMENT_SUMMARY = 'image_attachment_summary',
  INFO = 'info',
  INTENT_PREDICTION = 'intent_prediction',
  LANGUAGE_CHANGE = 'language_change',
  SET_CONTEXT_VARIABLE = 'set_context_variable',
  WARNING = 'warning',
}

export type WorkflowVersionType = 'auto_saved' | 'saved' | 'published';

export type PreviewMode = 'state' | 'classic' | 'version';

export interface IntentFilter {
  intentId: string;
  intentName: string;
  intentWorkflowId?: string;
  isAutoflow: boolean;
  isChecked: boolean;
  isDraft: boolean;
  isPublished?: boolean;
  previewMode?: PreviewMode;
  selectedVersion?: number | null;
  selectedVersionType?: WorkflowVersionType | null;
  versionType?: WorkflowVersionType;
}

export interface AiInstructionUIFilter {
  aiInstructionId: string;
  isDraft: boolean;
}

export interface ContentSnippetUIFilter {
  contentSnippetId: string;
  isDraft: boolean;
}

export interface WorkflowPreviewSliceState {
  aiInstructionFilters: AiInstructionUIFilter[] | null;
  apiPreview: ApiPreviewState;
  channel: BuilderView;
  contentSnippetsFilters: ContentSnippetUIFilter[] | null;
  conversationId: string | null;
  emailPreview: EmailPreviewState;
  /**
   * Whether ticket-creation handoffs really write to the helpdesk during
   * preview. Off by default -- previews must not create tickets unless the
   * builder explicitly asks for it.
   */
  executeTicketCreationInPreview: boolean;
  intentFilters: IntentFilter[];
  /**
   * The user's intent/subflow filter selections, cached per channel so they
   * survive leaving and re-entering preview. `intentFilters`/`subflowFilters`
   * remain the active channel's working copy -- these buckets are only the
   * persistence backing the preview page merges from on mount.
   */
  intentFiltersByChannel: Record<string, IntentFilter[]>;
  language: string;
  previewLogs: PreviewLogProps[];
  subflowFilters: IntentFilter[];
  subflowFiltersByChannel: Record<string, IntentFilter[]>;
  voicePreview: VoicePreviewState;
  workflowCvs: { name: string; value: ContextVariableValue }[];
  workflowTag: string | null;
}

export interface EmailPreviewState {
  awaitingSideConversationReply: boolean;
  emailConversationId: string | null;
  error: string | null;
  isLoading: boolean;
  responseEmail: string;
  responseEmailGroups: FormattedResponseItem[][];
  sideConversationRecipients: Record<string, string>;
  stage:
    | 'email-form'
    | 'email-template'
    | 'email-template-error'
    | 'interactive-widget';
  token: string | null;
}

interface VoicePreviewState {
  conversationId: string | null;
  mode: 'call' | 'sms_csat';
  outboundVersionSelection: {
    intentWorkflowId: string;
    version: number;
    versionType: WorkflowVersionType;
  } | null;
  outboundWorkflowId: string | null;
  previewPhoneNumber: string | null;
  webCallPreviewBrandId: string | null;
}

/**
 * Widget preview types
 */
export type PreviewFromMode = 'landing-page' | 'classic-workflow' | 'autoflow';

export type IntentFiltersMode =
  | 'live-traffic'
  | 'current-intent-and-live-traffic'
  | 'customized';

type PreviewLogVariant =
  | ArticleSuggestionStepPreviewLogProps
  | ApiStepPreviewLogProps
  | CodeExecutionStepPreviewLogProps
  | IntentPredictionPreviewLogProps
  | LanguageChangePreviewLogProps
  | AutoflowPreviewLogProps
  | InfoPreviewLogProps
  | GoToPreviewLogProps
  | AutoflowToolPreviewLogProps
  | AiInstructionPreviewLogProps
  | ContentSnippetPreviewLogProps
  | CustomEventPreviewLogProps
  | ImageAttachmentSummaryPreviewLogProps
  | SetContextVariablePreviewLogProps
  | ConditionPreviewLogProps
  | ErrorPreviewLogProps;

/**
 * `previewLogId` is stamped on by the slice as logs enter the store. Preview
 * logs have no server identity, and setPreviewLanguage removes an entry from
 * the middle of the list, so rendering by array index reassigns component
 * state to the wrong log. Client-side only -- never sent back to the server.
 */
export type PreviewLogProps = PreviewLogVariant & { previewLogId?: string };

/**
 * Shape the console falls back to for a log type this build cannot render.
 *
 * Deliberately not part of PreviewLogVariant: a member with a non-literal
 * `log_type` would defeat discriminated-union narrowing for every other case.
 * The console casts to this in its default branch instead.
 */
export interface UnknownPreviewLogProps {
  log_type: string;
  [key: string]: unknown;
}

export interface ArticleSuggestionStepPreviewLogProps {
  articles_predicted: string[];
  confidence_scores: number[];
  log_type: PreviewLogType.ARTICLE_SUGGESTION_STEP;
}

export interface ApiStepPreviewLogProps {
  action_name?: string;
  input_params: Record<string, string>;
  log_type: PreviewLogType.API_STEP;
  output_params: Record<string, string>;
  response_body?: unknown;
  response_status?: number | null;
  url_used?: string;
}

export interface CodeExecutionStepPreviewLogProps {
  code?: string;
  display_name?: string;
  input_params: Record<string, unknown>;
  log_type: PreviewLogType.CODE_EXECUTION_STEP;
  output_params: Record<string, unknown>;
}

export interface CustomEventPreviewLogProps {
  event_configuration_id: string;
  event_configuration_name: string;
  log_type: PreviewLogType.CUSTOM_EVENT;
  properties: Record<string, unknown>;
}

export interface IntentPredictionPreviewLogProps {
  autoflow_policy?: string;
  confidence_scores: number[];
  intents_predicted: string[];
  is_executing_autoflow?: boolean;
  is_executing_draft?: boolean;
  log_type: PreviewLogType.INTENT_PREDICTION;
  reasoning?: string | null;
  user_query?: string;
  version_name?: string;
}

export interface LanguageChangePreviewLogProps {
  language_code: string;
  log_type: PreviewLogType.LANGUAGE_CHANGE;
}

export interface AutoflowPreviewLogProps {
  agent_response?: string;
  agent_thoughts: string[];
  log_type: PreviewLogType.AUTOFLOW;
  tool_logs: (AutoflowToolPreviewLogProps | CustomEventPreviewLogProps)[];
  user_query?: string;
}

export interface InfoPreviewLogProps {
  log_type: PreviewLogType.INFO | PreviewLogType.WARNING;
  message?: string;
  metadata?: Record<string, unknown>;
  title: string;
}
export interface GoToPreviewLogProps {
  intent_id?: string;
  intent_title?: string;
  log_type: PreviewLogType.GOTO_STEP;
}

export interface AutoflowToolPreviewLogProps extends GenericToolPayload {
  log_type: PreviewLogType.AUTOFLOW_TOOL;
}

export interface AiInstructionPreviewLogProps {
  instructions: { ai_instruction_id: string; instruction: string }[];
  log_type: PreviewLogType.AI_INSTRUCTION;
}

export interface ContentSnippetPreviewLogProps {
  content_snippets: { content_snippet_id: string; snippet: string }[];
  log_type: PreviewLogType.CONTENT_SNIPPET;
}

export interface ImageAttachmentSummaryPreviewLogProps {
  image_url: string;
  log_type: PreviewLogType.IMAGE_ATTACHMENT_SUMMARY;
  summary: string;
}

export interface SetContextVariableEntry {
  context_variable_id: string;
  /** Resolved server-side, so no client lookup is needed. */
  display_name?: string | null;
  value: unknown;
}

export interface SetContextVariablePreviewLogProps {
  cv_display_names_and_values: SetContextVariableEntry[];
  log_type: PreviewLogType.SET_CONTEXT_VARIABLE;
}

export interface ConditionBranchProps {
  /** 1-based, matching the order branches are evaluated in. */
  index: number;
  /** Null for the catch-all branch, which has no condition to render. */
  expression?: string | null;
  matched: boolean;
}

export interface ConditionPreviewLogProps {
  branches: ConditionBranchProps[];
  /** Context variable id -> value the branches were evaluated against. */
  field_values: Record<string, unknown>;
  log_type: PreviewLogType.CONDITION;
  /** Null when no branch matched -- the "No Valid Condition" case. */
  matched_branch_index?: number | null;
}

/** Mirrors ErrorAlertSeverity server-side; absent for an uncatalogued message. */
export type PreviewLogErrorSeverity = 'high' | 'medium' | 'low';

/**
 * A recorded Solve error, mirrored from the same events that feed the Error
 * Logs page. Emitted for every record_solve_widget_error_event call made while
 * a preview conversation is active whose type the customer's Error Logs / API
 * Error Logs pages show, including the non-fatal ones the runtime swallows (a
 * failed JWT decode leaving its context variables empty, say), which
 * previously left no trace in the console at all.
 */
export interface ErrorPreviewLogProps {
  error_message: string;
  error_payload?: Record<string, unknown>;
  /** WorkflowErrorType, when the recording call site classified the error. */
  error_type?: string | null;
  log_type: PreviewLogType.ERROR;
  severity?: PreviewLogErrorSeverity | null;
  status_code?: number | null;
}

export type IntentFiltersForIFrame = Record<
  string,
  {
    is_autoflow: boolean;
    is_draft: boolean;
    selected_version?: number | null;
    selected_version_type?: WorkflowVersionType | null;
  }
>;

export type IntentFiltersForEmail = Record<string, { is_draft: boolean }>;

/**
 * Api preview types
 */
export interface ApiPreviewState {
  conversation_id: string | null;
  history: SolveApiTransaction[];
  isLoading: boolean;
  isStream: boolean;
}

export type PreviewVoiceTranscriptType = 'user' | 'bot' | 'preview-log';
export interface PreviewVoiceTranscript {
  isFinal?: boolean;

  payload?: VoiceTranscriptPayload;
  text: string;
  timestamp?: string;
  type: PreviewVoiceTranscriptType;
}

export type SolveApiTransaction =
  | HistoricalSolveApiRequest
  | SolveApiResponse
  | SolveApiStreamingResponse
  | ForethoughtError;

export interface SolveApiRequest {
  ai_instruction_filters_for_preview?:
    | {
        ai_instruction_id: string;
        is_draft: boolean;
      }[]
    | null;
  context_variables: Record<string, string>;
  conversation_id?: string;
  intent_filters_for_preview?: IntentFiltersForIFrame;
  is_preview: boolean;
  query?: string;
  solve_api_token: string;
  stream?: boolean;
}

export interface ForethoughtError {
  message: string;
  status: number;
}

export type HistoricalSolveApiRequest = Omit<
  SolveApiRequest,
  'solve_api_token' | 'intent_filters_for_preview' | 'is_preview'
>;

export function isHistoricalSolveApiRequest(
  message: unknown,
): message is HistoricalSolveApiRequest {
  return (message as HistoricalSolveApiRequest)?.stream !== undefined;
}

export function isSolveApiResponse(
  message: unknown,
): message is SolveApiResponse {
  return (message as SolveApiResponse)?.widget_components !== undefined;
}

export function isSolveApiStreamingResponse(
  message: SolveApiTransaction,
): message is SolveApiStreamingResponse {
  return (message as SolveApiStreamingResponse)?.requestId !== undefined;
}

export function isForethoughtError(error: unknown): error is ForethoughtError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  );
}

export enum Format {
  html = 'html',
  markdown = 'markdown',
}

export enum ComponentType {
  ARTICLE = 'article',
  ARTICLE_ANSWER = 'article_answer',
  BUTTON = 'button',
  DYNAMIC_CARD = 'dynamic_card',
  FORM = 'form',
  HYPERLINK_REDIRECT = 'hyperlink_redirect',
  IMAGE = 'image',
  SALESFORCE_HANDOFF = 'salesforce_handoff',
  TEXT = 'text',
  ZENDESK_HANDOFF = 'zendesk_handoff',
}

export interface TextApiWidgetComponent {
  format: Format;
  message: string;
  type: ComponentType.TEXT;
}

export interface Button {
  display_name: string;
  value: string;
}

export interface ButtonApiWidgetComponent {
  buttons: Button[];
  format: Format;
  message: string;
  output_variable: string;
  type: ComponentType.BUTTON;
}

export interface FormField {
  input_type: string;
  list_options: [];
  output_variable: string;
  placeholder: string;
}

export interface FormApiWidgetComponent {
  form_fields: FormField[];
  format: Format;
  message: string;
  type: ComponentType.FORM;
}

export interface Image {
  description: string;
  url: string;
}

export interface ImageApiWidgetComponent {
  images: Image[];
  type: ComponentType.IMAGE;
}

export interface Article {
  confidence_level: string;
  confidence_score: number;
  date_created: string;
  date_updated: string;
  document_id: string;
  full_html: string;
  link: string;
  preview: string;
  source_type: string;
  title: string;
}

export interface ArticleAnswerApiWidgetComponent {
  articles: Article[];
  paraphrased_answer: string;
  type: ComponentType.ARTICLE_ANSWER;
}

export interface ArticleApiWidgetComponent {
  articles: Article[];
  type: ComponentType.ARTICLE;
}

export interface HyperlinkRedirectApiWidgetComponent {
  destination_link: string;
  type: ComponentType.HYPERLINK_REDIRECT;
}

export interface DynamicCardApiWidgetComponent {
  cards: Array<{
    displayable_dynamic_sections: Array<{
      components: [{ label: string | null; value: string }];
      type: 'image' | 'title' | 'content';
    }>;
    value: string;
  }>;
  content_rows_limit?: number | null;
  message: string;
  output_variable: string;
  type: ComponentType.DYNAMIC_CARD;
}

export type DynamicCardTranscriptWidgetComponent = Pick<
  DynamicCardApiWidgetComponent,
  'cards' | 'message' | 'content_rows_limit'
>;

export interface BaseHandoffApiWidgetComponent {
  visitor_email: string | null;
  visitor_name: string | null;
  visitor_question: string | null;
}

export interface ZendeskApiWidgetComponent extends BaseHandoffApiWidgetComponent {
  type: ComponentType.ZENDESK_HANDOFF;
}

export interface SalesforceApiWidgetComponent extends BaseHandoffApiWidgetComponent {
  type: ComponentType.SALESFORCE_HANDOFF;
}

export type ApiWidgetComponent =
  | TextApiWidgetComponent
  | ButtonApiWidgetComponent
  | FormApiWidgetComponent
  | ImageApiWidgetComponent
  | HyperlinkRedirectApiWidgetComponent
  | ArticleAnswerApiWidgetComponent
  | ArticleApiWidgetComponent
  | ZendeskApiWidgetComponent
  | SalesforceApiWidgetComponent
  | DynamicCardApiWidgetComponent;
export interface SolveApiResponse {
  conversation_id: string;
  widget_components: ApiWidgetComponent[];
}

export interface SolveApiStreamingResponse extends SolveApiResponse {
  requestId: number;
}
